import { createSelector } from 'reselect';
import _flatten from 'lodash/flatten';
import _isEmpty from 'lodash/isEmpty';
import _isDate from 'lodash/isDate';
import { parseISO } from 'date-fns';
import parse from 'date-fns/parse';
import { FULL_ISO_DATE_FORMAT } from 'constants/logic';
import {
  instructorsData,
  scheduleData,
  schedulesInstances,
  schedulesClasses,
  scheduleInstance,
} from 'store/saga/entities/bound';

export const schedulesInstancesSelector = state =>
  schedulesInstances.loaded(state) ? schedulesInstances.data(state) : {};

const schedulesSelector = state => state.schedules;

export const selectSchedulesBoardStatusFilters = createSelector(
  schedulesSelector,
  schedules => schedules.schedulesBoardStatusFiltersKeys
);

export const selectSelectedClassIdFilter = createSelector(
  schedulesSelector,
  schedules => schedules.schedulesBoardClassesFilters
);

export const selectSelectedDurationFilter = createSelector(
  schedulesSelector,
  schedules => schedules.schedulesBoardDurationsFilters
);

export const selectSelectedInstructorIdFilter = createSelector(
  schedulesSelector,
  schedules => schedules.schedulesBoardInstructorsFilters
);

export const selectSchedulesBoardTypeFilters = createSelector(
  schedulesSelector,
  schedules => schedules.schedulesBoardTypeFiltersKeys
);

export const selectSchedulesBoardFiltersQueries = createSelector(schedulesSelector, schedules =>
  [
    ...schedules.schedulesBoardStatusFiltersQueries,
    ...schedules.schedulesBoardTypeFiltersQueries,
    ...schedules.schedulesBoardClassesFiltersQueries,
    ...schedules.schedulesBoardDurationsFiltersQueries,
    ...schedules.schedulesBoardInstructorsFiltersQueries,
  ].filter(Boolean)
);

export const selectSchedulesBoardNextDate = createSelector(
  schedulesSelector,
  schedules => schedules.nextDate && parseISO(schedules.nextDate)
);

export const selectSchedule = state => (scheduleData.loaded(state) ? scheduleData.data(state) : {});
export const selectScheduleIsLoading = state => scheduleData.loading(state);

export const selectSchedulesInstances = createSelector(
  state => state,
  selectSchedulesBoardStatusFilters,
  selectSchedulesBoardTypeFilters,
  (state, statusKeys, typeKeys) => {
    if (_isEmpty(statusKeys) || _isEmpty(typeKeys) || !schedulesInstances.loaded(state)) {
      return {};
    }

    const { instances } = schedulesInstances.data(state);
    return Object.entries(instances).reduce(
      (acc, [id, schedules]) => ({
        ...acc,
        [id]: schedules.map(({ startingAt, ...rest }) => ({
          ...rest,
          startingAt: _isDate(startingAt) ? startingAt : parse(startingAt, FULL_ISO_DATE_FORMAT, new Date()),
        })),
      }),
      {}
    );
  }
);

export const selectSelectedScheduleInstanceId = createSelector(
  schedulesSelector,
  schedules => schedules.selectedScheduleInstanceId
);

export const selectSelectedScheduleInstance = state => {
  const schedulesInstancesData = selectSchedulesInstances(state);
  const instances = Object.entries(schedulesInstancesData).map(([, value]) => value);
  const selectedScheduleInstanceId = selectSelectedScheduleInstanceId(state);
  return _flatten(instances).find(instance => instance.id === selectedScheduleInstanceId) || {};
};

export const selectSchedulesInstancesIsLoading = state => schedulesInstances.loading(state);

export const selectSchedulesClasses = (state, venueId) =>
  schedulesClasses.loaded(state, venueId) ? schedulesClasses.data(state, venueId) : [];

export const selectSchedulesClassesIsLoading = (state, venueId) => schedulesClasses.loading(state, venueId);
export const selectSchedulesClassesIsLoaded = (state, venueId) => schedulesClasses.loaded(state, venueId);

export const selectSchedulesInstructors = (state, venueId) =>
  instructorsData.loaded(state, venueId) ? instructorsData.data(state, venueId) : [];

export const selectCreatedClassId = createSelector(schedulesSelector, schedules => schedules.createdClassId);

export const selectCreatedInstructorId = createSelector(schedulesSelector, schedules => schedules.createdInstructorId);

export const selectFirstInstanceOfSchedule = (state, scheduleId) => {
  const schedulesInstancesData = selectSchedulesInstances(state);
  const instances = Object.entries(schedulesInstancesData).map(([, value]) => value);
  return _flatten(instances).find(({ schedule }) => schedule.id === scheduleId) || {};
};

export const selectScheduleInstance = state => (scheduleInstance.loaded(state) ? scheduleInstance.data(state) : {});
export const selectScheduleInstanceIsLoading = state => scheduleInstance.loading(state);
